import type { ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/modern/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { Button } from "@/modern/components/ui/button-shadcn";

/** Stacked above FileDetail (+10) and standalone Adjust Points Dialog. */
export const SPOT_INCENTIVE_DIALOG_Z = "z-[calc(var(--swivu-bryntum-z-index)+20)]";

export type SpotIncentivePickerPresentation = "dialog" | "inline";

interface SpotIncentivePickerShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  /** `dialog` = stacked modal (default). `inline` = fill parent (File Detail Adjust Points rail). */
  presentation?: SpotIncentivePickerPresentation;
  size?: {
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    height?: string;
    minHeight?: string;
    maxHeight?: string;
  };
  footer: ReactNode;
  headerEnd?: ReactNode;
  wrapBody?: boolean;
  dialogClassName?: string;
  children: ReactNode;
}

function PickerBody({
  wrapBody,
  children,
  className,
}: {
  wrapBody: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain",
        wrapBody && "px-5 pb-4 pt-4 sm:px-6",
        className,
      )}
    >
      {children}
    </div>
  );
}

function PickerFooter({ footer, className }: { footer: ReactNode; className?: string }) {
  return (
    <div className={cn("shrink-0 border-t border-border/40 px-5 py-4 sm:px-6", className)}>
      {footer}
    </div>
  );
}

/**
 * Shared shell for Spot Incentive pickers (task, resources, points).
 * Dialog for standalone modal; inline for File Detail Adjust Points rail.
 */
export function SpotIncentivePickerShell({
  open,
  onClose,
  title,
  description,
  presentation = "dialog",
  size = {},
  footer,
  headerEnd,
  wrapBody = true,
  dialogClassName,
  children,
}: SpotIncentivePickerShellProps) {
  if (presentation === "inline") {
    if (!open) return null;

    return (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
        <span className="sr-only">
          {title}. {description}
        </span>
        {headerEnd ? (
          <div className="flex shrink-0 justify-end px-0.5 pb-2">{headerEnd}</div>
        ) : null}
        <PickerBody wrapBody={wrapBody} className={wrapBody ? "px-0.5 sm:px-0.5" : undefined}>
          {children}
        </PickerBody>
        {/* Match embedded Adjust Points Save footer: border-t + pt-4, pinned to rail bottom */}
        <PickerFooter footer={footer} className="border-t border-border/40 px-0 py-0 pt-4 sm:px-0" />
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        dialogOverlayClassName={SPOT_INCENTIVE_DIALOG_Z}
        className={cn(
          SPOT_INCENTIVE_DIALOG_Z,
          "flex max-h-[min(90vh,46rem)] w-[min(48rem,92vw)] max-w-[48rem] flex-col gap-0 overflow-hidden",
          "border-border/60 bg-background p-0 shadow-2xl sm:rounded-[2rem]",
          dialogClassName,
        )}
        style={{
          width: size.width,
          minWidth: size.minWidth,
          maxWidth: size.maxWidth,
          height: size.height && size.height !== "auto" ? size.height : undefined,
          minHeight: size.minHeight,
          maxHeight: size.maxHeight,
        }}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>

        <div className="flex shrink-0 items-start gap-3 px-5 pt-5 sm:px-6">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-normal tracking-tight text-foreground sm:text-2xl">{title}</h2>
            <p className="mt-0.5 text-sm font-normal text-muted-foreground">{description}</p>
          </div>
          {headerEnd}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onClose}
            aria-label={`Close ${title}`}
            className="size-9 shrink-0 rounded-full border-input bg-muted text-foreground/80 shadow-sm hover:border-input hover:text-foreground"
          >
            <X className="size-4" />
          </Button>
        </div>

        <PickerBody wrapBody={wrapBody}>{children}</PickerBody>
        <PickerFooter footer={footer} />
      </DialogContent>
    </Dialog>
  );
}

/** @deprecated Overlay is always Dialog; prop ignored. */
export type SpotIncentiveOverlayVariant = "float" | "dialog";
